import {
  type LngLatBoundsLike,
  // Aliased to avoid shadowing the global `Map` (matches how Map.tsx imports react-map-gl's).
  Map as MapGL,
  type MapPadding,
  type MapPaddingObject,
  MapPopup,
  Marker,
  type ViewStateChangeEvent,
} from "@ui/components/Map";
import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Supercluster from "supercluster";
import { FRAME_TOPBAR_HEIGHT_VAR } from "@/lib/frame";
import { type Bbox, bboxToFitBounds } from "@/lib/where-bounding-boxes";
import type { MapBounds, MapCluster } from "@/types/search-map";

// Mapbox bills per `Map` initialisation. One `mapboxgl.Map` is lazily mounted inside
// `PersistentLayout` on the first consumer that calls `useMap`, then teleported into
// each consumer's slot for the rest of the session. Subsequent consumers (any kind of
// map, any page) reuse the same instance — zero additional loads.

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
// Below `lg` the map is full-bleed under the top bar (see `MobileSearchMap`) — corners are either
// off-screen or behind the results sheet, so it is square. At `lg+` it sits in the `SearchMapSlot` card.
const SEARCH_MAP_CLASSNAME = "size-full overflow-hidden rounded-none lg:rounded-3xl";
const FIT_PADDING = 60;

interface MapViewState {
  className?: string;
  fitBounds?: LngLatBoundsLike;
  fitBoundsPadding?: MapPadding;
  recenterTo?: { longitude: number; latitude: number; padding?: MapPaddingObject };
  onMove?: (event: ViewStateChangeEvent) => void;
  onUserMoveEnd?: (event: ViewStateChangeEvent) => void;
  mapStyle?: string;
  interactive?: boolean;
  showControls?: boolean;
  showFullscreen?: boolean;
  controlsPosition?: "top-right" | "bottom-right";
  cooperativeGestures?: boolean;
}

const DEFAULT_VIEW: MapViewState = {};

interface MapHostContextValue {
  attach: (slot: HTMLElement) => void;
  detach: () => void;
  setOverlay: (overlay: ReactNode) => void;
  setView: (view: MapViewState) => void;
  markMounted: () => void;
}

const MapHostContext = createContext<MapHostContextValue | null>(null);

export function MapHost({ children }: { children: ReactNode }) {
  const [hasMounted, setHasMounted] = useState(false);
  const [overlay, setOverlay] = useState<ReactNode>(null);
  const [view, setView] = useState<MapViewState>(DEFAULT_VIEW);
  const offscreenRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pendingSlotRef = useRef<HTMLElement | null>(null);

  const moveWrapperTo = useCallback((target: HTMLElement) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    target.appendChild(wrapper);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  }, []);

  // Ref order in a single commit fires children before parents, so a consumer's slot
  // ref can run before MapHost's own wrapper ref. Stash the slot and retry from
  // `setWrapperRef` once the wrapper is mounted.
  const attach = useCallback(
    (slot: HTMLElement) => {
      if (!wrapperRef.current) {
        pendingSlotRef.current = slot;
        return;
      }
      pendingSlotRef.current = null;
      moveWrapperTo(slot);
    },
    [moveWrapperTo],
  );

  const detach = useCallback(() => {
    pendingSlotRef.current = null;
    const offscreen = offscreenRef.current;
    if (offscreen) moveWrapperTo(offscreen);
  }, [moveWrapperTo]);

  const setWrapperRef = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el;
      if (!el) return;
      const pending = pendingSlotRef.current;
      if (pending) {
        pendingSlotRef.current = null;
        moveWrapperTo(pending);
      }
    },
    [moveWrapperTo],
  );

  const markMounted = useCallback(() => setHasMounted(true), []);

  const ctxValue = useMemo<MapHostContextValue>(
    () => ({ attach, detach, setOverlay, setView, markMounted }),
    [attach, detach, markMounted],
  );

  return (
    <MapHostContext.Provider value={ctxValue}>
      {children}
      <div
        ref={offscreenRef}
        aria-hidden
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          width: 320,
          height: 320,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div ref={setWrapperRef} style={{ width: "100%", height: "100%" }}>
          {hasMounted && (
            <MapGL
              className={view.className}
              controlsPosition={view.controlsPosition}
              cooperativeGestures={view.cooperativeGestures}
              fitBounds={view.fitBounds}
              fitBoundsPadding={view.fitBoundsPadding}
              recenterTo={view.recenterTo}
              interactive={view.interactive}
              mapStyle={view.mapStyle}
              mapboxAccessToken={MAPBOX_TOKEN}
              onMove={view.onMove}
              onUserMoveEnd={view.onUserMoveEnd}
              showControls={view.showControls}
              showFullscreen={view.showFullscreen}
            >
              {overlay}
            </MapGL>
          )}
        </div>
      </div>
    </MapHostContext.Provider>
  );
}

interface UseMapInput {
  overlay?: ReactNode;
  className?: string;
  fitBounds?: LngLatBoundsLike;
  fitBoundsPadding?: MapPadding;
  recenterTo?: { longitude: number; latitude: number; padding?: MapPaddingObject };
  onUserBoundsChange?: (bounds: MapBounds) => void;
  /** Reports the map viewport (zoom + bounds) ~per frame during moves, for live client clustering. */
  onViewportChange?: (viewport: { zoom: number; bounds: MapBounds }) => void;
  mapStyle?: string;
  interactive?: boolean;
  showControls?: boolean;
  showFullscreen?: boolean;
  controlsPosition?: "top-right" | "bottom-right";
  cooperativeGestures?: boolean;
}

export function useMap(input: UseMapInput): (el: HTMLDivElement | null) => void {
  const ctx = useContext(MapHostContext);
  if (!ctx) throw new Error("useMap requires a MapHost ancestor");

  const { attach, detach, setOverlay, setView, markMounted } = ctx;
  const {
    overlay,
    className,
    fitBounds,
    fitBoundsPadding,
    recenterTo,
    onUserBoundsChange,
    onViewportChange,
    mapStyle,
    interactive,
    showControls,
    showFullscreen,
    controlsPosition,
    cooperativeGestures,
  } = input;

  const onUserMoveEnd = useMemo<((e: ViewStateChangeEvent) => void) | undefined>(() => {
    if (!onUserBoundsChange) return undefined;
    return (event) => {
      const bbox = event.target.getBounds();
      if (!bbox) return;
      const ne = bbox.getNorthEast();
      const sw = bbox.getSouthWest();
      onUserBoundsChange({
        northEast: { lat: ne.lat, lng: ne.lng },
        southWest: { lat: sw.lat, lng: sw.lng },
      });
    };
  }, [onUserBoundsChange]);

  // Report the viewport (zoom + bounds) on every move — user or programmatic — throttled to one
  // update per animation frame so the client clusterer re-clusters continuously (and smoothly) as
  // the map zooms/pans, without a per-frame React storm.
  const rafRef = useRef<number | null>(null);
  const latestMoveRef = useRef<ViewStateChangeEvent | null>(null);
  const onMove = useMemo<((e: ViewStateChangeEvent) => void) | undefined>(() => {
    if (!onViewportChange) return undefined;
    return (event) => {
      latestMoveRef.current = event;
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const latest = latestMoveRef.current;
        if (!latest) return;
        const bbox = latest.target.getBounds();
        if (!bbox) return;
        const ne = bbox.getNorthEast();
        const sw = bbox.getSouthWest();
        onViewportChange({
          zoom: latest.viewState.zoom,
          bounds: { northEast: { lat: ne.lat, lng: ne.lng }, southWest: { lat: sw.lat, lng: sw.lng } },
        });
      });
    };
  }, [onViewportChange]);

  useEffect(() => {
    markMounted();
    setOverlay(overlay);
    setView({
      className,
      fitBounds,
      fitBoundsPadding: fitBoundsPadding ?? FIT_PADDING,
      recenterTo,
      onMove,
      onUserMoveEnd,
      mapStyle,
      interactive,
      showControls,
      showFullscreen,
      controlsPosition,
      cooperativeGestures,
    });
  }, [
    markMounted,
    setOverlay,
    setView,
    overlay,
    className,
    fitBounds,
    fitBoundsPadding,
    recenterTo,
    onMove,
    onUserMoveEnd,
    mapStyle,
    interactive,
    showControls,
    showFullscreen,
    controlsPosition,
    cooperativeGestures,
  ]);

  useEffect(
    () => () => {
      setOverlay(null);
      setView(DEFAULT_VIEW);
    },
    [setOverlay, setView],
  );

  return useCallback(
    (el: HTMLDivElement | null) => {
      if (el) attach(el);
      else detach();
    },
    [attach, detach],
  );
}

interface UseSearchResultsMapInput<THit, TId> {
  /** Fine-grained feed points for the FULL match set (from `useSearchMapPins`); clustered here. */
  points: MapCluster<THit>[];
  /** Resolve a hit's stable id (destinations `hit.id`, programmes `hit.sqid`). */
  getHitId: (hit: THit) => TId;
  renderPin: (hit: THit, close: () => void) => ReactNode;
  /** Glyph rendered inside a single-listing pin when it has no price label. */
  pinIcon: ReactNode;
  /** Label for a single-listing pin (the listing name); null → fall back to `pinIcon`. */
  pinLabel?: (point: MapCluster<THit>) => string | null;
  selectedHitId: TId | null;
  onSelectHit: (id: TId | null) => void;
  /** Hovered listing id (from a card or a pin) — highlights the matching pin. */
  hoveredHitId?: TId | null;
  onHoverHit?: (id: TId | null) => void;
  onUserBoundsChange: (bounds: MapBounds) => void;
  /** Map-pins fetch in flight — suppresses the empty state while pins load. */
  isLoading?: boolean;
  /** Map-pins fetch failed — suppresses the empty state so an error doesn't read as "no results". */
  isError?: boolean;
  /** Message shown centred on the map when there are no pins in view. */
  emptyLabel?: string;
  boundsSource: "where" | "map" | null;
  whereBbox: Bbox | null;
  /** "inline" (desktop): anchored MapPopup + zoom/fullscreen controls. "sheet" (mobile): no
   *  popup, no controls; the caller renders `selectedHit` as a floating card above the sheet. */
  variant?: "inline" | "sheet";
  /** fitBounds padding forwarded to the map — object form reserves the lower band for the sheet. */
  fitBoundsPadding?: MapPadding;
  /** When set (sheet mode), selecting a pin eases it clear of this occluded region (peek + card). */
  selectionPadding?: MapPaddingObject;
}

/** Result of {@link useSearchResultsMap}: the teleport slot ref plus the cache-surviving selected
 *  hit (non-null only in sheet mode, where the caller renders it as a floating pin card). */
interface UseSearchResultsMapResult<THit> {
  slotRef: (el: HTMLDivElement | null) => void;
  selectedHit: THit | null;
}

function computeBoundsFromPoints(points: { lat: number; lon: number }[]): LngLatBoundsLike | undefined {
  const first = points[0];
  if (!first) return undefined;
  let minLat = first.lat;
  let maxLat = first.lat;
  let minLon = first.lon;
  let maxLon = first.lon;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }
  if (minLat === maxLat && minLon === maxLon) {
    const pad = 0.5;
    return [
      [minLon - pad, minLat - pad],
      [maxLon + pad, maxLat + pad],
    ];
  }
  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

const CLUSTER_BUBBLE_STYLE: CSSProperties = {
  display: "flex",
  minWidth: 34,
  height: 34,
  alignItems: "center",
  justifyContent: "center",
  padding: "0 10px",
  borderRadius: 9999,
  border: "2px solid #ffffff",
  background: "#3f6212",
  color: "#ffffff",
  fontSize: 13,
  fontWeight: 600,
  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
};

// A single-listing pin renders as a labelled pill (the listing name): a white pill that flips to a
// filled state when selected/hovered. Long names truncate with an ellipsis.
const PIN_PILL_STYLE: CSSProperties = {
  display: "block",
  maxWidth: 168,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  padding: "5px 10px",
  borderRadius: 9999,
  border: "1px solid rgba(0,0,0,0.08)",
  background: "#ffffff",
  color: "#1a2e05",
  fontSize: 13,
  fontWeight: 600,
  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
};

const PIN_PILL_ACTIVE_STYLE: CSSProperties = {
  ...PIN_PILL_STYLE,
  background: "#3f6212",
  color: "#ffffff",
  border: "1px solid #3f6212",
};

const MAP_EMPTY_OVERLAY_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
};

const MAP_EMPTY_PILL_STYLE: CSSProperties = {
  padding: "10px 16px",
  borderRadius: 12,
  background: "#ffffff",
  color: "#1a2e05",
  fontSize: 14,
  fontWeight: 600,
  boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
};

type PinPointProps<THit> = { pointKey: string; count: number; hit?: THit };
type PinClusterProps = { sum: number };

export function useSearchResultsMap<THit, TId>(
  input: UseSearchResultsMapInput<THit, TId>,
): UseSearchResultsMapResult<THit> {
  const {
    points,
    getHitId,
    renderPin,
    pinIcon,
    pinLabel,
    selectedHitId,
    onSelectHit,
    hoveredHitId,
    onHoverHit,
    onUserBoundsChange,
    isLoading,
    isError,
    emptyLabel,
    boundsSource,
    whereBbox,
    variant = "inline",
    fitBoundsPadding,
    selectionPadding,
  } = input;

  const pointsBounds = useMemo(
    () => computeBoundsFromPoints(points.map((point) => ({ lat: point.lat, lon: point.lon }))),
    [points],
  );

  // Build the client-side clusterer once per point feed. `sum` aggregates the underlying listing
  // counts (a feed point can already stand for several co-located listings).
  const supercluster = useMemo(() => {
    const sc = new Supercluster<PinPointProps<THit>, PinClusterProps>({
      radius: 60,
      maxZoom: 20,
      map: (props) => ({ sum: props.count }),
      reduce: (acc, props) => {
        acc.sum += props.sum;
      },
    });
    sc.load(
      points.map((point) => ({
        type: "Feature" as const,
        properties: { pointKey: point.key, count: point.count, hit: point.hit },
        geometry: { type: "Point" as const, coordinates: [point.lon, point.lat] },
      })),
    );
    return sc;
  }, [points]);

  // Live viewport (updated ~per frame during moves) drives client clustering, so zooming re-clusters
  // instantly with no network re-tile.
  const [viewport, setViewport] = useState<{ zoom: number; bounds: MapBounds } | null>(null);

  const clusterFeatures = useMemo(() => {
    if (!viewport) return [];
    const { bounds, zoom } = viewport;
    const bbox: [number, number, number, number] = [
      bounds.southWest.lng,
      bounds.southWest.lat,
      bounds.northEast.lng,
      bounds.northEast.lat,
    ];
    return supercluster.getClusters(bbox, Math.floor(zoom));
  }, [supercluster, viewport]);

  // Camera intent is a single value updated only on deliberate changes (initial framing, Where
  // selection, cluster zoom-in) — never on incremental refetches — so the map stays put while the
  // user explores. A `fitBounds` change animates; the very first one snaps (see Map.tsx).
  const [pendingFit, setPendingFit] = useState<LngLatBoundsLike | undefined>(undefined);
  const didInitialFitRef = useRef(false);
  const prevWhereBboxRef = useRef(whereBbox);
  // Read the latest envelope without making it an effect dependency (else the Where-fit would
  // re-fire on every incremental point refetch).
  const pointsBoundsRef = useRef(pointsBounds);
  pointsBoundsRef.current = pointsBounds;

  // Initial fit to all results — once, and only when the user hasn't already taken over the map
  // (a pan before pins load flips boundsSource to "map") and no Where is driving its own fit.
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (boundsSource === "map" || boundsSource === "where" || whereBbox != null) return;
    if (pointsBounds) {
      didInitialFitRef.current = true;
      setPendingFit(pointsBounds);
    }
  }, [pointsBounds, boundsSource, whereBbox]);

  // A Where selection frames its bbox; clearing the Where (bbox goes from set to null while the user
  // isn't mid-pan) re-frames the full result set instead of leaving the map stuck on the old area.
  useEffect(() => {
    const prev = prevWhereBboxRef.current;
    prevWhereBboxRef.current = whereBbox;
    if (whereBbox && boundsSource === "where") {
      setPendingFit(bboxToFitBounds(whereBbox));
    } else if (prev != null && whereBbox == null && boundsSource !== "map" && pointsBoundsRef.current) {
      setPendingFit(pointsBoundsRef.current);
    }
  }, [whereBbox, boundsSource]);

  // Esc closes an open pin popup (the popup itself has no focusable close affordance). Capture-phase
  // + preventDefault so it closes the popup FIRST and doesn't also exit a fullscreen map on the same
  // keypress (Map.tsx's fullscreen handler bails when defaultPrevented).
  useEffect(() => {
    if (selectedHitId == null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        onSelectHit(null);
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [selectedHitId, onSelectHit]);

  // Zoom into a cluster by framing its members (supercluster's leaves), which re-clusters them into
  // sub-clusters / individual pins at the finer zoom.
  const zoomToCluster = useCallback(
    (clusterId: number) => {
      const leaves = supercluster.getLeaves(clusterId, Number.POSITIVE_INFINITY);
      const bounds = computeBoundsFromPoints(
        leaves.map((leaf) => ({ lat: leaf.geometry.coordinates[1] ?? 0, lon: leaf.geometry.coordinates[0] ?? 0 })),
      );
      if (bounds) setPendingFit(bounds);
    },
    [supercluster],
  );

  // The selected listing's popup resolves from the full point feed (always present) with a cached
  // fallback, so it survives re-clustering or a bounds refetch dropping it from the current view.
  const liveSelectedPoint = useMemo(
    () =>
      selectedHitId != null
        ? (points.find((point) => point.hit != null && getHitId(point.hit) === selectedHitId) ?? null)
        : null,
    [points, selectedHitId, getHitId],
  );
  const lastSelectedRef = useRef<MapCluster<THit> | null>(null);
  if (liveSelectedPoint) lastSelectedRef.current = liveSelectedPoint;
  const cachedSelected = lastSelectedRef.current;
  const selectedPoint =
    liveSelectedPoint ??
    (selectedHitId != null && cachedSelected?.hit != null && getHitId(cachedSelected.hit) === selectedHitId
      ? cachedSelected
      : null);

  // Sheet mode only: ease the map so a newly-selected pin surfaces above the sheet + floating card.
  // Fire once per selection (guarded by a ref) on the id transition — NOT on `selectedPoint`
  // identity, which churns on every background pins refetch and would fight the user's pan.
  const [recenterTo, setRecenterTo] = useState<
    { longitude: number; latitude: number; padding?: MapPaddingObject } | undefined
  >(undefined);
  const recenteredForIdRef = useRef<TId | null>(null);
  useEffect(() => {
    if (!selectionPadding) return;
    if (selectedHitId == null) {
      recenteredForIdRef.current = null;
      return;
    }
    if (recenteredForIdRef.current === selectedHitId) return;
    if (!selectedPoint) return;
    recenteredForIdRef.current = selectedHitId;
    setRecenterTo({ longitude: selectedPoint.lon, latitude: selectedPoint.lat, padding: selectionPadding });
  }, [selectedHitId, selectionPadding, selectedPoint]);

  const markers = useMemo(
    () =>
      clusterFeatures.map((feature) => {
        const lng = feature.geometry.coordinates[0] ?? 0;
        const lat = feature.geometry.coordinates[1] ?? 0;
        const props = feature.properties as PinPointProps<THit> & {
          cluster?: boolean;
          cluster_id?: number;
          point_count?: number;
          sum?: number;
        };

        // Cluster bubble — the number is the total listings it contains; click frames its members.
        if (props.cluster) {
          const clusterId = props.cluster_id ?? 0;
          const total = props.sum ?? props.point_count ?? 0;
          return (
            <Marker anchor="center" key={`cluster-${clusterId}`} latitude={lat} longitude={lng}>
              <button
                aria-label={`Zoom into ${total} results`}
                onClick={(event) => {
                  event.stopPropagation();
                  zoomToCluster(clusterId);
                }}
                style={{ appearance: "none", border: "none", background: "none", padding: 0, cursor: "pointer" }}
                type="button"
              >
                <div style={CLUSTER_BUBBLE_STYLE}>{total}</div>
              </button>
            </Marker>
          );
        }

        const single = props.count === 1 && props.hit != null;
        const id = single && props.hit != null ? getHitId(props.hit) : null;
        const active = id != null && (id === selectedHitId || id === hoveredHitId);
        const label = single
          ? (pinLabel?.({ key: props.pointKey, lat, lon: lng, count: props.count, hit: props.hit }) ?? null)
          : null;
        const ariaLabel = single
          ? label != null
            ? `View listing, ${label}`
            : "View listing"
          : `Zoom into ${props.count} results`;
        const activate = () => {
          if (single && props.hit != null) onSelectHit(getHitId(props.hit));
          // A co-located point (several listings within the feed cell) can't split further — nudge in.
          else {
            setPendingFit([
              [lng - 0.05, lat - 0.05],
              [lng + 0.05, lat + 0.05],
            ]);
          }
        };
        return (
          <Marker
            anchor={single && label == null ? "bottom" : "center"}
            key={`pt-${props.pointKey}`}
            latitude={lat}
            longitude={lng}
            style={{ zIndex: active ? 2 : undefined }}
          >
            {/* A real <button>: focusable + Enter/Space-activatable for free, with hover sync to cards. */}
            <button
              aria-label={ariaLabel}
              onClick={(event) => {
                event.stopPropagation();
                activate();
              }}
              onMouseEnter={id != null ? () => onHoverHit?.(id) : undefined}
              onMouseLeave={id != null ? () => onHoverHit?.(null) : undefined}
              style={{
                appearance: "none",
                border: "none",
                background: "none",
                padding: 0,
                cursor: "pointer",
                transform: active ? "scale(1.08)" : undefined,
                transition: "transform 120ms ease",
              }}
              type="button"
            >
              {single ? (
                label != null ? (
                  <div style={active ? PIN_PILL_ACTIVE_STYLE : PIN_PILL_STYLE}>{label}</div>
                ) : (
                  pinIcon
                )
              ) : (
                <div style={CLUSTER_BUBBLE_STYLE}>{props.count}</div>
              )}
            </button>
          </Marker>
        );
      }),
    [clusterFeatures, getHitId, selectedHitId, hoveredHitId, pinLabel, onSelectHit, onHoverHit, zoomToCluster, pinIcon],
  );

  // Sheet mode renders the selected hit as a floating card above the sheet (returned below), so the
  // anchored in-map popup is suppressed there.
  const popup = useMemo(
    () =>
      variant === "inline" && selectedPoint?.hit != null ? (
        <MapPopup
          key="popup"
          latitude={selectedPoint.lat}
          longitude={selectedPoint.lon}
          onClose={() => onSelectHit(null)}
        >
          {renderPin(selectedPoint.hit, () => onSelectHit(null))}
        </MapPopup>
      ) : null,
    [variant, selectedPoint, onSelectHit, renderPin],
  );

  const overlay = useMemo(
    () => (
      <>
        {markers}
        {popup}
        {!isLoading && !isError && points.length === 0 && emptyLabel ? (
          <div style={MAP_EMPTY_OVERLAY_STYLE}>
            <div style={MAP_EMPTY_PILL_STYLE}>{emptyLabel}</div>
          </div>
        ) : null}
      </>
    ),
    [markers, popup, isLoading, isError, points.length, emptyLabel],
  );

  const slotRef = useMap({
    overlay,
    className: SEARCH_MAP_CLASSNAME,
    fitBounds: pendingFit,
    fitBoundsPadding,
    recenterTo,
    onUserBoundsChange,
    onViewportChange: setViewport,
    // Sheet mode is chromeless: the bottom sheet is the expand affordance and pinch/pan suffice.
    showControls: variant === "inline",
    showFullscreen: variant === "inline",
  });

  return { slotRef, selectedHit: selectedPoint?.hit ?? null };
}

export function SearchMapSlot({ slotRef }: { slotRef: (el: HTMLDivElement | null) => void }) {
  return (
    <div
      // `sticky` creates a stacking context; raise it above the result cards (z-20)
      // so the map's expanded (fixed) view, trapped in this context, covers them.
      // `pt-6` clears the sticky top-bar's bottom border by the same 24px gap the
      // results column uses, so the map panel and the cards align below the border.
      className="sticky z-30 hidden pt-6 pr-8 pb-8 lg:block"
      style={{ top: FRAME_TOPBAR_HEIGHT_VAR, height: `calc(100dvh - ${FRAME_TOPBAR_HEIGHT_VAR})` }}
    >
      <div ref={slotRef} className="size-full" />
    </div>
  );
}
